import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/pro-regular-svg-icons";
import RoundedRectangle from "../../../RoundedRectangle";
import EmblemImage from "../../../EmblemImage";
import ScaleFade from "../../../Animated/ScaleFade";
import { MatchLocationEnum } from "../../../../../client/api";
import { selectIsLoggedIn } from "../../../../../selectors";
import type { AppDispatch, RootState } from "../../../../../store";
import { fetchMatchBetVoting, voteMatchBet } from "../../../../../store/actions/matchActions";
import { ClavaContext } from "../../../../../config/contexts";
import type { BetPanelProps } from "../../../../../config/types";

const BetPanel: React.FC<BetPanelProps> = ({ matchId, team1Name, team2Name, isFutureMatch, team1Emblem, team2Emblem }) => {
  useContext(ClavaContext);
  const dispatch = useDispatch<AppDispatch>();
  const loggedIn = useSelector(selectIsLoggedIn);
  const matchBet = useSelector((s: RootState) =>
    (s.match.matchBets ?? []).find((b) => b.id === matchId)
  );

  const loadVoting = useCallback(() => {
    if (!matchBet && loggedIn) {
      fetchMatchBetVoting(dispatch, matchId);
    }
  }, [dispatch, matchBet, matchId, loggedIn]);

  useEffect(() => {
    loadVoting();
  }, [loadVoting]);

  const voting = matchBet?.response;

  const canVote = useMemo(() => {
    const userVoting = voting?.userVoting ?? null;
    return loggedIn && isFutureMatch && !userVoting;
  }, [loggedIn, isFutureMatch, voting]);

  const handleVote = useCallback(
    (bet: MatchLocationEnum) => {
      if (!canVote) return;
      voteMatchBet(dispatch, matchId, bet);
    },
    [canVote, dispatch, matchId]
  );

  if (!loggedIn) return null;

  const userVoting = voting?.userVoting ?? null;

  const options = [
    {
      key: "home",
      label: team1Name,
      value: voting?.voting1 ?? 0,
      type: MatchLocationEnum.HOME,
      emblem: team1Emblem,
    },
    {
      key: "draw",
      label: "X",
      value: voting?.x ?? 0,
      type: MatchLocationEnum.ALL,
      emblem: undefined,
    },
    {
      key: "away",
      label: team2Name,
      value: voting?.voting2 ?? 0,
      type: MatchLocationEnum.AWAY,
      emblem: team2Emblem,
    },
  ] as const;

  return (
    <RoundedRectangle
      className="w-full overflow-hidden bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark"
      hasBorder
      hasTitle
      titleType="upLeft"
      titleKey="yourBet"
    >
      <div className="px-4 pt-4 pb-4 flex flex-col gap-5">
        <div className="flex items-end justify-between gap-4">
          {options.map((o) => {
            const isSelected = userVoting === o.type;
            const showVoting = !isFutureMatch || !!userVoting;
            const value = Math.max(0, Math.min(100, o.value));
            const bottomBorderColorClass = isSelected ? "text-primary-50 dark:text-primary-50-dark" : "text-light-gray dark:text-light-gray-dark";
            const topBorderColorClass = isSelected ? "text-primary" : "text-font-600 dark:text-font-600-dark";
            const percentageTextColor = isSelected ? "text-primary" : "text-font-600 dark:text-font-600-dark";
            const radius = 28;
            const circumference = 2 * Math.PI * radius;
            const strokeDasharray = showVoting && value > 0 ? `${(value / 100) * circumference} ${circumference}` : "0";
            
            return (
              <div key={o.key} className="flex flex-col items-center gap-1 flex-1">
                {showVoting && (
                  <span className={`text-sm font-medium ${percentageTextColor}`}>
                    {value.toFixed(0)}%
                  </span>
                )}
                <button
                  type="button"
                  disabled={!canVote}
                  onClick={() => handleVote(o.type)}
                  className={`relative w-16 h-16 rounded-full ${!canVote ? "cursor-default" : "cursor-pointer"}`}
                >
                  <svg className="absolute inset-0 w-full h-full z-10 transform -rotate-90" viewBox="0 0 64 64" preserveAspectRatio="xMidYMid meet">
                    <circle
                      cx="32"
                      cy="32"
                      r={radius}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="4"
                      className={bottomBorderColorClass}
                    />
                      {showVoting && value > 0 && (
                      <circle
                        cx="32"
                        cy="32"
                        r={radius}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="4"
                        strokeDasharray={strokeDasharray}
                        strokeLinecap="round"
                        className={topBorderColorClass}
                      />
                    )}
                  </svg>
                    <div className="absolute inset-0 flex items-center justify-center z-30">
                    {o.key === "draw" ? (
                      <ScaleFade isOpen hover>
                        <FontAwesomeIcon
                          icon={faClose}
                          className={`w-6 h-6 ${isSelected ? "text-primary" : "text-font-600 dark:text-font-600-dark"}`}
                        />
                      </ScaleFade>
                    ) : (
                      <ScaleFade isOpen hover>
                        <EmblemImage
                          image={o.emblem}
                          width={32}
                          round
                        />
                      </ScaleFade>
                    )}
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </RoundedRectangle>
  );
};

export default BetPanel;